import { useMemo, useState } from 'react';

import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom, useResetAtom } from 'jotai/utils';

import {
    ANGLE_MEASUREMENTS,
    DISPLACEMENT_MEASUREMENTS,
    JOINT_ANGLE_MEASUREMENTS,
    DISTANCE_MEASUREMENTS,
    PATHS,
    SWING_PLANES,
    VISUALIZATION_AXES,
    VisualizationMeasurement,
    VisualizationPath,
    VisualizationPlane,
} from '@elva-visualizer';
import { useTranslate } from '@i18n';
import { pathsAtom, selectedVisualizationAxesAtom, swingPlanesAtom, traceAtom } from '@store';
import { Box, Button, Checkbox, colors, ModalBase, ModalBody, ModalHeader, ScribbleIcon, Tabs, Text, u } from '@ui';

import { Pill } from '../../PillGroup/PillGroup';

const VISUALIZATION_TABS = {
    DISPLACEMENT: 'displacement',
    ANGLES: 'angles',
    DISTANCE: 'distance',
    SWING_PLANE: 'swing-plane',
    PATHS: 'paths',
} as const;

type VisualizationTab = (typeof VISUALIZATION_TABS)[keyof typeof VISUALIZATION_TABS];

type VizualizationTabItem = {
    id: VisualizationTab;
    label: string;
};

const tabItems: Array<VizualizationTabItem> = [
    { id: VISUALIZATION_TABS.DISPLACEMENT, label: 'Displacement' },
    { id: VISUALIZATION_TABS.ANGLES, label: 'Angles' },
    { id: VISUALIZATION_TABS.DISTANCE, label: 'Distance' },
    { id: VISUALIZATION_TABS.SWING_PLANE, label: 'Swing Plane' },
    { id: VISUALIZATION_TABS.PATHS, label: 'Paths' },
];

const swingPlaneIndex = 3;
const pathIndex = 4;

const visualizationGroups: Record<
    string,
    Record<string, VisualizationMeasurement> | Record<string, VisualizationPlane> | Record<string, VisualizationPath>
> = {
    [VISUALIZATION_TABS.DISPLACEMENT]: DISPLACEMENT_MEASUREMENTS,
    [VISUALIZATION_TABS.ANGLES]: { ...ANGLE_MEASUREMENTS, ...JOINT_ANGLE_MEASUREMENTS },
    [VISUALIZATION_TABS.DISTANCE]: DISTANCE_MEASUREMENTS,
    [VISUALIZATION_TABS.SWING_PLANE]: SWING_PLANES,
    [VISUALIZATION_TABS.PATHS]: PATHS,
} as const;

const MAX_SELECTED = 6;
const MAX_TRACES = 2;

const selectedVisualizationCountAtom = atom((get) => get(selectedVisualizationAxesAtom).length);
const selectedTraceCountAtom = atom((get) => get(traceAtom).length);

const sortByLabel = (a: [string, VisualizationMeasurement], b: [string, VisualizationMeasurement]): number =>
    a[1].label.localeCompare(b[1].label);

type VisualizationAxisProps = {
    axisId: string;
};

const VisualizationAxis = ({ axisId }: VisualizationAxisProps) => {
    const setSelectedAxis = useSetAtom(selectedVisualizationAxesAtom);

    const canSelect = useAtomValue(selectedVisualizationCountAtom) < MAX_SELECTED;
    const isSelectedAtom = useMemo(
        () => selectAtom(selectedVisualizationAxesAtom, (ids) => ids.includes(axisId)),
        [axisId],
    );
    const isSelected = useAtomValue(isSelectedAtom);
    const axis = VISUALIZATION_AXES[axisId];
    if (!axis) return null;
    return (
        <Pill
            key={axisId}
            isSelected={isSelected}
            label={axis.label}
            isDisabled={!canSelect && !isSelected}
            onClick={() =>
                setSelectedAxis((prev) =>
                    prev.includes(axisId) ? prev.filter((id) => id !== axisId) : [...prev, axisId],
                )
            }
        ></Pill>
    );
};

type VisualizationProps = {
    id: string;
    label: string;
    axes: readonly string[];
};

const Visualization = ({ id, label, axes }: VisualizationProps) => {
    const [traceIds, setTraceIds] = useAtom(traceAtom);

    const canSelect = useAtomValue(selectedTraceCountAtom) < MAX_TRACES;
    return (
        <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            justifyContent="space-between"
            px={u(2)}
            gap={u(1)}
            py={u(1)}
            style={{ backgroundColor: colors.bluegray[200], borderRadius: u(1) }}
        >
            <Text variant="h3">{label}</Text>

            <Box display="flex" alignItems="center" gap={u(1)}>
                {axes.map((axisId) => (
                    <VisualizationAxis key={axisId} axisId={axisId} />
                ))}
                <Pill
                    isSelected={traceIds.includes(id)}
                    label={'Trace'}
                    isDisabled={!canSelect && !traceIds.includes(id)}
                    onClick={() =>
                        setTraceIds((prev) =>
                            prev.includes(id) ? prev.filter((plane) => plane !== id) : [...prev, id],
                        )
                    }
                    left={<ScribbleIcon />}
                />
            </Box>
        </Box>
    );
};

const Plane = ({ id, label }: Record<'id' | 'label', string>) => {
    const [swingPlanes, setSwingPlanes] = useAtom(swingPlanesAtom);
    return (
        <Checkbox
            label={label}
            checked={swingPlanes.includes(id)}
            onChange={() =>
                setSwingPlanes((prev) => (prev.includes(id) ? prev.filter((plane) => plane !== id) : [...prev, id]))
            }
            fullWidth
        />
    );
};

const Path = ({ id, label }: Record<'id' | 'label', string>) => {
    const [paths, setPaths] = useAtom(pathsAtom);
    return (
        <Checkbox
            label={label}
            checked={paths.includes(id)}
            onChange={() =>
                setPaths((prev) => (prev.includes(id) ? prev.filter((path) => path !== id) : [...prev, id]))
            }
            fullWidth
        />
    );
};

type VisualizationsModalProps = {
    onClose: () => void;
};

export const VisualizationsModal = ({ onClose }: VisualizationsModalProps) => {
    const selectedCount = useAtomValue(selectedVisualizationCountAtom);
    const resetSelected = useResetAtom(selectedVisualizationAxesAtom);
    const [activeId, setActiveId] = useState<VisualizationTab>(VISUALIZATION_TABS.DISPLACEMENT);
    const paths = useAtomValue(pathsAtom);
    const swingPlanes = useAtomValue(swingPlanesAtom);

    const tabItemsWithBadges = tabItems.map((tabItem, index) => {
        if (index === swingPlaneIndex && swingPlanes.length) {
            return { ...tabItem, badge: swingPlanes.length };
        }
        if (index === pathIndex && paths.length) {
            return { ...tabItem, badge: paths.length };
        }
        return tabItem;
    });

    const visualizationMenu = visualizationGroups[activeId] ?? {};
    const t = useTranslate();
    const traceCount = useAtomValue(selectedTraceCountAtom);
    const resetTraces = useResetAtom(traceAtom);

    return (
        <ModalBase open variant="partial" onDismiss={onClose}>
            <ModalHeader>
                <Box display="flex" gap={u(2)}>
                    <Text variant="h3">{t('common.visualizations')}</Text>
                    {!!selectedCount && (
                        <Box position="relative" style={{ top: -u(1), height: u(2) }}>
                            <Button
                                size="small"
                                variant={selectedCount < MAX_SELECTED ? 'secondary' : 'destroy'}
                                rounded
                                icon="close"
                                onClick={() => resetSelected()}
                            >
                                {selectedCount}/{MAX_SELECTED} selected
                            </Button>
                        </Box>
                    )}
                    {!!traceCount && (
                        <Box position="relative" style={{ top: -u(1), height: u(2) }}>
                            <Button
                                size="small"
                                variant={traceCount < MAX_TRACES ? 'secondary' : 'destroy'}
                                rounded
                                icon="close"
                                onClick={() => resetTraces()}
                            >
                                {traceCount}/{MAX_TRACES} traces
                            </Button>
                        </Box>
                    )}
                </Box>
            </ModalHeader>
            <ModalBody>
                <Tabs
                    items={tabItemsWithBadges}
                    activeId={activeId}
                    onClick={(tab) => setActiveId(tab as VisualizationTab)}
                />
            </ModalBody>
            <ModalBody py={u(4)} flexShrink={1}>
                <Box display="flex" flexDirection="column" gap={u(2)}>
                    {Object.entries(visualizationMenu)
                        .sort(sortByLabel)
                        .map(
                            ([id, visualization]: [
                                string,
                                VisualizationMeasurement | VisualizationPlane | VisualizationPath,
                            ]) => {
                                if (visualization.type === 'plane') {
                                    return <Plane key={id} {...visualization} />;
                                }
                                if (visualization.type === 'path') {
                                    return <Path key={id} {...visualization} />;
                                }
                                if (visualization.type === 'measurement') {
                                    return <Visualization key={id} id={id} {...visualization} />;
                                }

                                return null;
                            },
                        )}
                </Box>
            </ModalBody>
        </ModalBase>
    );
};
